import React from 'react';
import {Skeleton} from 'antd';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import Box from '../../../../../common/Layout/Box';
import AppText from '../../../../../common/AppText';
import {t} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {
    FREQUENCY_COLUMN_LABEL_KEYS,
    SESSION_PER_WEEK_OPTIONS,
} from '../../../../../../../web/src/components/Track/TrackConsts';

const FILTER_TAG_WIDTHS = [58, 73, 49];

const TABLE_BORDER_COLOR = '#E0E0E0';
const TABLE_HEADER_BG = '#f0f0f0';
const TABLE_GROUP_BG = '#f9f9f9';
const NAME_COLUMN_MIN_WIDTH = 502;
const COLUMN_WIDTH = 80;
const TABLE_HEADER_HEIGHT = 45;
const TABLE_ROW_HEIGHT = 49;
const TABLE_CELL_PADDING_INLINE = 20;
const TABLE_ROW_PADDING_BLOCK = 16;
const TABLE_GROUP_PADDING = '8px 20px 9px';

const TABLE_SKELETON_GROUPS = [
    {groupLabelWidth: 73, rowNameWidths: [238, 260, 311]},
    {groupLabelWidth: 80, rowNameWidths: [235, 349, 335]},
    {groupLabelWidth: 94, rowNameWidths: [286, 285, 261]},
];

const getGridTemplateColumns = (columnCount) =>
    `${NAME_COLUMN_MIN_WIDTH}px repeat(${columnCount}, ${COLUMN_WIDTH}px)`;

const FilterTagSkeleton = ({width}) => (
    <Skeleton.Button
        active
        shape={'round'}
        style={{width: `${width}px`, minWidth: `${width}px`, height: 32, padding: 0}}
    />
);

const SkeletonBar = ({width, height = 16}) => (
    <Box style={{width: `${width}px`, flexShrink: 0, display: 'block'}}>
        <Skeleton.Input active block style={{height, borderRadius: 4}} />
    </Box>
);

const SkeletonCircle = () => (
    <Skeleton.Avatar active size={16} shape={'circle'} />
);

const cellBorderInlineStart = {borderInlineStart: `1px solid ${TABLE_BORDER_COLOR}`};
const cellBorderBottom = {borderBottom: `1px solid ${TABLE_BORDER_COLOR}`};

const CourseLimitationsSkeleton = ({showCategoryFilter}) => {
    const gridTemplateColumns = getGridTemplateColumns(SESSION_PER_WEEK_OPTIONS.length);
    const tableMinWidth = NAME_COLUMN_MIN_WIDTH + (COLUMN_WIDTH * SESSION_PER_WEEK_OPTIONS.length);

    return (
        <VStack space={'0.75rem'} width={'100%'}>
            {showCategoryFilter &&
                <VStack space={'0.5rem'}>
                    <AppText fontSize={'12px'} mediumFont color={Colors.newDesign.mainText} style={{letterSpacing: '0.3px'}}>
                        {t('courseLimitationsFilterByCategory')}
                    </AppText>
                    <HStack space={'0.5rem'} flexWrap={'wrap'} style={{rowGap: '0.5rem'}}>
                        {FILTER_TAG_WIDTHS.map((width, idx) => (
                            <FilterTagSkeleton key={idx} width={width} />
                        ))}
                    </HStack>
                </VStack>
            }
            <VStack space={'0.5rem'}>
                <AppText fontSize={'12px'} mediumFont color={Colors.newDesign.mainText} style={{letterSpacing: '0.3px'}}>
                    {t('courseLimitationsFilterByServiceType')}
                </AppText>
                <HStack space={'0.5rem'} flexWrap={'wrap'} style={{rowGap: '0.5rem'}}>
                    {FILTER_TAG_WIDTHS.map((width, idx) => (
                        <FilterTagSkeleton key={idx} width={width} />
                    ))}
                </HStack>
            </VStack>
            <Box
                width={'100%'}
                style={{
                    display: 'block',
                    border: `1px solid ${TABLE_BORDER_COLOR}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                }}
            >
                <Box overflowX={'auto'} width={'100%'} style={{display: 'block'}}>
                    <Box minWidth={`${tableMinWidth}px`} width={'100%'} style={{display: 'block'}}>
                        <Box
                            style={{
                                display: 'grid',
                                gridTemplateColumns,
                                backgroundColor: TABLE_HEADER_BG,
                                height: TABLE_HEADER_HEIGHT,
                                ...cellBorderBottom,
                            }}
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingInlineStart: TABLE_CELL_PADDING_INLINE,
                                }}
                            >
                                <AppText fontSize={'14px'} mediumFont color={Colors.newDesign.mainText}>
                                    {t('courseLimitationsCourseName')}
                                </AppText>
                            </Box>
                            {SESSION_PER_WEEK_OPTIONS.map(frequency => (
                                <Box
                                    key={frequency}
                                    style={{
                                        ...cellBorderInlineStart,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <AppText fontSize={'14px'} mediumFont color={Colors.newDesign.mainText} style={{textAlign: 'center'}}>
                                        {t(FREQUENCY_COLUMN_LABEL_KEYS[frequency])}
                                    </AppText>
                                </Box>
                            ))}
                        </Box>

                        {TABLE_SKELETON_GROUPS.map((group, groupIdx) => (
                            <Box key={groupIdx} width={'100%'} style={{display: 'block'}}>
                                <Box
                                    style={{
                                        display: 'block',
                                        backgroundColor: TABLE_GROUP_BG,
                                        padding: TABLE_GROUP_PADDING,
                                        ...cellBorderBottom,
                                    }}
                                >
                                    <SkeletonBar width={group.groupLabelWidth} height={12} />
                                </Box>
                                {group.rowNameWidths.map((nameWidth, rowIdx) => (
                                    <Box
                                        key={rowIdx}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns,
                                            height: TABLE_ROW_HEIGHT,
                                            ...cellBorderBottom,
                                        }}
                                    >
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingBlock: TABLE_ROW_PADDING_BLOCK,
                                                paddingInlineStart: TABLE_CELL_PADDING_INLINE,
                                                paddingInlineEnd: TABLE_CELL_PADDING_INLINE,
                                            }}
                                        >
                                            <SkeletonBar width={nameWidth} />
                                        </Box>
                                        {SESSION_PER_WEEK_OPTIONS.map(frequency => (
                                            <Box
                                                key={frequency}
                                                style={{
                                                    ...cellBorderInlineStart,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <SkeletonCircle />
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </VStack>
    );
};

export default CourseLimitationsSkeleton;
